import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import Loaders from "@stadium/ui/src/components/loaders/loaders";
import { InternalButton } from "@components/shared/buttons";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { US_ISO } from "@stadium/constants";
import useGetUserWarehouseAddress from "@stadium/hooks/lib/get-user-warehouse-address";
import { useFlags } from "@stadium/services/launch-darkly";
import { threePlWarehouseUpdatesFlag } from "@stadium/services/launch-darkly/constants";
import { useCheckoutContext as useGlobalCheckoutContext } from "@stadium/checkout/src/contexts";
import { useCheckoutContext } from "@contexts/checkout-context";
import useOnCheckout from "@hooks/checkout/use-on-checkout";

import { getCountry } from "@selectors/country";
import {
  getDisplayWarehouseAddress,
  getIsLockerShop,
  getIsPricelessShop,
  getStoreCurrencyCode,
  getStoreCurrencyDetails,
  getIsBillingNoteOnSelfOrder,
  getIsHideCountryFlag,
  getAllowCorporateBilling,
  getIsBillingNoteRequired,
  getAdditionalBillingNote,
} from "@selectors/store";
import {
  getConversionRates,
  getUsCountryId,
  makeTaxNumberRequiredForCountry,
  getWarehouseAddressId,
} from "@stadium/redux-utils/selectors/configSelector";
import { convertPrice } from "@stadium/helpers/currency";
import {
  getCartCountryId,
  getCartOrderAllowAnnonymousInvite,
  getCartOrderFetched,
  getCartOrderShipAddressId,
  getIsEligibleForPomp,
  getReceivedItemsCount,
} from "@selectors/cart";

import CheckoutStepsMain from "@stadium/checkout/src/components/checkout-steps-wrapper";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { StoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import {
  SELECT_ADDRESS_STEP_STATE,
  POMP_STEP_STATE,
  PAYMENT_SELECTION_STEP_STATE,
  BILLING_DETAILS_STEP_STATE,
} from "@stadium/checkout/src/constants/order";
import {
  BILLING_COMP_COUNTRY,
  BILLING_COMP_NOTE,
  BILLING_COMP_TAX,
  BILLING_COMP_ZIPCODE,
} from "@stadium/checkout/src/components/billing-details";
import { POINTS_CURRENCY_CODE } from "@constants/store";

import styles from "./main.module.scss";

const CHECKOUT_STEPS = [SELECT_ADDRESS_STEP_STATE, POMP_STEP_STATE, PAYMENT_SELECTION_STEP_STATE];

const CheckoutSteps = () => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const isLockerShop = useSelector(getIsLockerShop);

  const {
    setSelectedAddress,
    currency,
    billingTaxNumber,
    selectedAddress,
    storeSpecificPointsBalance,
    stadiumPointsBalance,
  } = useGlobalCheckoutContext();

  const {
    cartTotal,
    youPayAmount,
    savedCreditCards,
    teamWallets,
    refetchCreditCards,
    cartItemsQuantity,
    loading: isCreditCardsLoading,
  } = useCheckoutContext();

  const { convertPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();

  const { isGlobalCountry } = useContext(StoreContext);

  const displayWarehouseAddress = useSelector(getDisplayWarehouseAddress);
  const isOrderFetched = useSelector(getCartOrderFetched);
  const cartCountryId = useSelector(getCartCountryId);
  const isEligibleForPomp = useSelector(getIsEligibleForPomp);
  const storeDetails = useSelector(getStoreCurrencyDetails);
  const selectedCountry = useSelector(getCountry);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const receivedItemsCount = useSelector(getReceivedItemsCount);
  const billingNoteOnSelfOrder = useSelector(getIsBillingNoteOnSelfOrder);
  const orderShipAddressId = useSelector(getCartOrderShipAddressId);
  const hideCountryFlag = useSelector(getIsHideCountryFlag);
  const corporateBillingEnabled = useSelector(getAllowCorporateBilling);
  const allowAnonymousInvite = useSelector(getCartOrderAllowAnnonymousInvite);
  const isBillingNoteRequired = useSelector(getIsBillingNoteRequired);
  const additionalBillingNote = useSelector(getAdditionalBillingNote);

  const usCountryId = useConfig(getUsCountryId);
  const conversionRates = useConfig(getConversionRates);
  const warehouseAddressId = useConfig(getWarehouseAddressId);

  const showWarehouseAddress =
    displayWarehouseAddress && (cartCountryId === usCountryId || isGlobalCountry);

  const { warehouseDetails, loading: warehouseAddressLoading } = useGetUserWarehouseAddress({
    country: selectedCountry?.iso || US_ISO,
    skip: !showWarehouseAddress,
  });

  const { [threePlWarehouseUpdatesFlag]: isThreePlWarehouseUpdatesEnabled } = useFlags();

  const storeBalance = convertPriceInDisplayStoreCurrency(storeSpecificPointsBalance);
  const stadiumPoints = convertPriceInDisplayStoreCurrency(
    stadiumPointsBalance,
    POINTS_CURRENCY_CODE,
  );

  const selectTaxNumberRequiredForCountry = useMemo(
    () => makeTaxNumberRequiredForCountry(cartCountryId),
    [cartCountryId],
  );

  const isStoreBalanceEnough = useMemo(
    () => storeBalance + stadiumPoints >= cartTotal,
    [storeBalance, cartTotal, stadiumPoints],
  );

  const taxNumberRequired = useConfig(selectTaxNumberRequiredForCountry);

  const isWarehouseAddressSelected =
    warehouseAddressId === orderShipAddressId ||
    (isThreePlWarehouseUpdatesEnabled && selectedAddress?.isWarehouseAddress);

  const { previewMode } = usePreviewModeContext();

  const { loading: isCheckoutLoading, onCheckout } = useOnCheckout({
    billingTaxNumber,
  });

  const isLockerWarehouseOrder = useMemo(
    () => isLockerShop && isWarehouseAddressSelected,
    [isLockerShop, isWarehouseAddressSelected],
  );

  const needsTaxNumber = useMemo(
    () => !isLockerWarehouseOrder && taxNumberRequired,
    [isLockerWarehouseOrder, taxNumberRequired],
  );

  const isBillingDetailsStepRequired = useMemo(
    () => isLockerWarehouseOrder || needsTaxNumber || (billingNoteOnSelfOrder && !isPricelessShop),
    [isLockerWarehouseOrder, needsTaxNumber, billingNoteOnSelfOrder, isPricelessShop],
  );

  const checkoutSteps = useMemo(() => {
    let checkoutSteps = [];

    if (isPricelessShop) checkoutSteps = [SELECT_ADDRESS_STEP_STATE];
    else if (isEligibleForPomp) checkoutSteps = CHECKOUT_STEPS;
    else checkoutSteps = [SELECT_ADDRESS_STEP_STATE, PAYMENT_SELECTION_STEP_STATE];

    if (isBillingDetailsStepRequired) {
      checkoutSteps.push(BILLING_DETAILS_STEP_STATE);
    }

    return checkoutSteps;
  }, [
    isEligibleForPomp,
    isPricelessShop,
    isLockerShop,
    isWarehouseAddressSelected,
    isBillingDetailsStepRequired,
  ]);

  const lockerShopBillingComponents = useMemo(
    () => [
      BILLING_COMP_COUNTRY,
      BILLING_COMP_ZIPCODE,
      ...(billingNoteOnSelfOrder ? [BILLING_COMP_NOTE] : []),
      ...(taxNumberRequired ? [BILLING_COMP_TAX] : []),
    ],
    [taxNumberRequired, billingNoteOnSelfOrder],
  );

  const billingStepProps = useMemo(() => {
    if (isLockerWarehouseOrder)
      return {
        components: lockerShopBillingComponents,
        isBillingNoteRequired: billingNoteOnSelfOrder && isBillingNoteRequired,
        additionalBillingNote,
      };

    let components = [];

    if (needsTaxNumber) components.push(BILLING_COMP_TAX);
    if (billingNoteOnSelfOrder) components.push(BILLING_COMP_NOTE);

    return {
      components,
      isBillingNoteStepOptional:
        !needsTaxNumber && billingNoteOnSelfOrder && !isBillingNoteRequired,
      additionalBillingNote,
      isBillingNoteRequired: billingNoteOnSelfOrder && isBillingNoteRequired,
    };
  }, [
    isLockerWarehouseOrder,
    needsTaxNumber,
    billingNoteOnSelfOrder,
    isBillingNoteRequired,
    additionalBillingNote,
  ]);

  const optionalSteps = useMemo(() => {
    if (billingStepProps.isBillingNoteStepOptional) return [BILLING_DETAILS_STEP_STATE];
    return [];
  }, [billingStepProps]);

  const cartTotalInSelectedCurrency = convertPrice({
    from: storeCurrencyCode,
    to: currency?.code,
    value: cartTotal,
    conversionRates,
  });

  const handlePlaceOrder = () =>
    onCheckout({
      currencyCodeId: currency?.id,
      selectedAddress: selectedAddress,
    });

  const renderPlaceOrderCta = ({ isDisabled = false } = {}) => (
    <div className={styles.placeOrderBtn}>
      <InternalButton
        styleType="primary"
        disabled={
          !selectedAddress?.externalId || +receivedItemsCount !== +cartItemsQuantity || isDisabled
        }
        onClick={handlePlaceOrder}
        isLoading={isCheckoutLoading}
      >
        Place Order
      </InternalButton>
    </div>
  );

  return (
    <div>
      {!isMobileDevice && <h1 className={styles.pageHeading}>Checkout</h1>}
      {!isOrderFetched || (showWarehouseAddress && warehouseAddressLoading) ? (
        <div>
          <Loaders.PageLoader wrapperClass="pageLoaderwrap" />
        </div>
      ) : (
        <CheckoutStepsMain
          canHaveOptionalSteps
          checkoutSteps={checkoutSteps}
          optionalSteps={optionalSteps}
          selectedCountry={selectedCountry}
          showWarehouseAddress={showWarehouseAddress}
          withCorporateBilling={corporateBillingEnabled && !allowAnonymousInvite}
          isDefaultAddressWarehouse={isLockerShop && showWarehouseAddress}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          savedCreditCards={savedCreditCards}
          refetchCreditCards={refetchCreditCards}
          isCreditCardsLoading={isCreditCardsLoading}
          teamWallets={teamWallets}
          storeDetails={storeDetails}
          orderTotal={cartTotalInSelectedCurrency}
          payableAmount={youPayAmount}
          isDisabled={previewMode}
          billingDetailsAdditionalProps={billingStepProps}
          renderCustomContinueCta={isPricelessShop ? renderPlaceOrderCta : null}
          hideCountryFlag={hideCountryFlag}
          isCreditCardsDefaultCollapsed={isStoreBalanceEnough}
          warehouseDetails={warehouseDetails}
        />
      )}
    </div>
  );
};

export default CheckoutSteps;
